'use client';

import type { MouseEvent } from 'react';
import { ChevronRight, Podcast } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { cn, numberFormatter } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { formatCompactValue, getPricePositive } from '@/lib/utils/number';
import { useRightDrawer } from '@/contexts/DrawerContext';
import type { CryptoKlinePoint } from '@/type/crypto';
import MarketDetail from '../predic/detail/market/MarketDetail';
import NewsDetail from '../predic/detail/News';
import MarketTopic from '../predic/detail/event/MarketTopic';
import NewsCard from '../predic/cards/NewsCard';
import { ChangeText } from '../predic/cards/ChangeText';
import CryptoContainer from '../predic/entity/CryptoPage';
import { WorldCupMatchCard, WorldCupMatchCardProps } from '../predic/world-cup/card';
import type { WorldCupMatchCardData } from '../predic/types/market';
import { MarketCard } from '../predic/cards/market';
import WorldCupPage from '../predic/WorldCupPage';
import PortfolioPage from '../predic/PortfolioPage';
import { getMarketOutcome, getMarketStatus, isMarketResolved } from '../predic/utils/market';

export type UserContextType =
  | 'market'
  | 'portfolio'
  | 'news'
  | 'market_topic'
  | 'crypto'
  | 'world_cup';

interface UserContextCardProps {
  userContext: any;
  contextType: UserContextType;
}

const CONTEXT_CARD_CLASS =
  'overflow-hidden border border-[#1E1E1E] bg-[#121212] max-w-125 min-w-90 rounded-xl';
const IMAGE_FIELDS = ['image_url', 'image', 'icon', 'market_image', 'market_icon'] as const;

const currencyFormatter = (number: number) =>
  numberFormatter(number, {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    trailingZeroDisplay: 'stripIfInteger',
  });

function toNumber(value: number | string | null | undefined) {
  const parsed = Number(typeof value === 'string' ? value.replace(/[$,%\s,]/g, '') : value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function findImageUrl(value: unknown, seen = new WeakSet<object>()): string | null {
  if (!value || typeof value !== 'object') return null;
  if (seen.has(value)) return null;
  seen.add(value);

  const record = value as Record<string, unknown>;
  for (const field of IMAGE_FIELDS) {
    const image = record[field];
    if (typeof image === 'string' && image.length > 0) return image;
  }

  for (const child of Object.values(record)) {
    if (Array.isArray(child)) {
      for (const item of child) {
        const image = findImageUrl(item, seen);
        if (image) return image;
      }
    } else if (child && typeof child === 'object') {
      const image = findImageUrl(child, seen);
      if (image) return image;
    }
  }

  return null;
}

export default function UserContextCard({ userContext, contextType }: UserContextCardProps) {
  const trendingCard = userContext?.trending_card;

  switch (contextType) {
    case 'market': {
      const marketContext = userContext;
      return (
        <div className={CONTEXT_CARD_CLASS}>
          <MarketContent event={marketContext.event} markets={marketContext.markets} />
        </div>
      );
    }
    case 'portfolio':
      return (
        <div className={CONTEXT_CARD_CLASS}>
          <PortfolioContent portfolio={trendingCard ?? userContext.portfolioData} />
        </div>
      );
    case 'market_topic':
      return (
        <div className={CONTEXT_CARD_CLASS}>
          <MarketTopicContent event={trendingCard?.event ?? trendingCard ?? userContext} />
        </div>
      );
    case 'news':
      return (
        <div className={CONTEXT_CARD_CLASS}>
          <NewsContent news={trendingCard?.news ?? userContext} />
        </div>
      );
    case 'crypto':
      return (
        <div className={CONTEXT_CARD_CLASS}>
          <CryptoContent crypto={trendingCard ?? userContext} />
        </div>
      );
    case 'world_cup': {
      const worldCupContext = toWorldCupContext(trendingCard ?? userContext);
      return worldCupContext ? <WorldCapContent {...worldCupContext} /> : null;
    }
    default:
      return null;
  }
}

function WorldCapContent(props: WorldCupMatchCardProps) {
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const pathname = usePathname();
  const unlinkable = pathname.startsWith('/predic/world-cup/');

  const handleLink = (e: MouseEvent<HTMLDivElement>) => {
    if (!unlinkable) {
      e.preventDefault();
      setDrawerWidth(600);
      openDrawer(<WorldCupPage openInSidebar eventSlug={props.slug} />);
    }
  };

  return (
    <div className={CONTEXT_CARD_CLASS} onClick={handleLink}>
      <WorldCupMatchCard
        {...props}
        linkEnabled={false}
        showBookmark={false}
        showLiveBadge={false}
      />
    </div>
  );
}

function toWorldCupContext(context: any): WorldCupMatchCardData | null {
  if (!context || typeof context !== 'object') return null;

  const teams = Array.isArray(context.teams)
    ? (context.teams as Array<Record<string, unknown>>)
    : null;
  const homeTeam =
    teams?.find((team) => team.ordering === 'home') ?? context.home_team ?? context.homeTeam;
  const awayTeam =
    teams?.find((team) => team.ordering === 'away') ?? context.away_team ?? context.awayTeam;

  if (!homeTeam?.name || !awayTeam?.name) return null;

  return {
    slug: context.slug ?? context.eventSlug ?? '',
    market_event_id: context.market_event_id ?? context.marketEventId ?? context.eventId,
    status_label: context.status_label ?? context.statusLabel ?? context.title,
    volume: formatWorldCupVolume(context.volume ?? context.totalVolume),
    draw_probability: formatWorldCupProbability(
      context.draw_probability ?? context.drawProbability ?? context.draw,
    ),
    home_team: {
      name: homeTeam.name,
      probability: formatWorldCupProbability(homeTeam.probability ?? homeTeam.yesPrice),
      probability_class_name: homeTeam.probability_class_name ?? homeTeam.probabilityClassName,
      image_src: homeTeam.image_src ?? homeTeam.imageSrc ?? homeTeam.logo,
      avatar_fallback: homeTeam.avatar_fallback ?? homeTeam.avatarFallback,
    },
    away_team: {
      name: awayTeam.name,
      probability: formatWorldCupProbability(awayTeam.probability ?? awayTeam.yesPrice),
      probability_class_name: awayTeam.probability_class_name ?? awayTeam.probabilityClassName,
      image_src: awayTeam.image_src ?? awayTeam.imageSrc ?? awayTeam.logo,
      avatar_fallback: awayTeam.avatar_fallback ?? awayTeam.avatarFallback,
    },
  };
}

function formatWorldCupProbability(value: unknown) {
  const parsed = toNumber(value as string | number | null | undefined);
  if (!Number.isFinite(parsed) || parsed <= 0) return '--';
  if (parsed > 1) return `${parsed.toFixed(1)}%`;
  return `${(parsed * 100).toFixed(1)}%`;
}

function formatWorldCupVolume(value: unknown) {
  const parsed = toNumber(value as string | number | null | undefined);
  if (!Number.isFinite(parsed) || parsed <= 0) return '$0';
  if (parsed >= 1_000_000) return `$${(parsed / 1_000_000).toFixed(2)}M`;
  if (parsed >= 1_000) return `$${(parsed / 1_000).toFixed(2)}K`;
  return `$${parsed.toFixed(0)}`;
}

function CryptoContent({ crypto }: { crypto: any }) {
  const { t } = useTranslation();
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const pathname = usePathname();
  const symbol = (crypto?.symbol || crypto?.token || 'BTC').toUpperCase();
  const name = crypto?.name || symbol;
  const price = toNumber(crypto?.price ?? crypto?.priceText);
  const change24h = toNumber(
    crypto?.percent_change_24h ?? crypto?.percentChange24h ?? crypto?.changeText ?? crypto?.change,
  );
  const sparklineData = normalizeCryptoSparkline(
    crypto?.kline ?? crypto?.kline_24h ?? crypto?.kline24h ?? crypto?.history,
  );
  const unlinkable = pathname.startsWith('/predic/crypto');

  const handleLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!unlinkable) {
      e.preventDefault();
      setDrawerWidth(600);
      openDrawer(<CryptoContainer symbol={symbol} openInSidebar />);
    }
  };

  return (
    <Link
      href={`/predic/crypto${crypto?.symbol ? `/${crypto.symbol}` : ''}`}
      className={cn('block overflow-hidden rounded-xl border border-[#1E1E1E] bg-[#121212] p-4', {
        'cursor-default': unlinkable,
      })}
      onClick={handleLink}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            {crypto?.logo_url || crypto?.logoUrl || crypto?.icon ? (
              <img
                src={crypto.logo_url || crypto.logoUrl || crypto.icon}
                alt=""
                className="size-10 rounded-full object-cover"
              />
            ) : (
              <span className="flex size-10 items-center justify-center rounded-full bg-[#F7931A] text-[22px] font-bold leading-none text-white">
                ₿
              </span>
            )}
            <div className="min-w-0">
              <div className="truncate font-medium leading-6 text-white">{name}</div>
              <div className="truncate text-sm leading-4 text-[#919FA6]">{symbol}</div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <div className="text-[20px] font-semibold leading-6 text-white">
              {currencyFormatter(price)}
            </div>
            <div className="flex items-center gap-1">
              <ChangeText
                value={change24h}
                className="text-xs font-medium leading-6"
                showTriangle={false}
              />
              <span className="text-xs font-medium leading-6 text-[#697479]">({t('24h')})</span>
            </div>
          </div>
        </div>

        <div className="w-[44%] shrink-0 self-end">
          <CryptoSparkline
            data={sparklineData}
            direction={change24h >= 0 ? 'up' : 'down'}
            className="h-15 w-full"
          />
        </div>
      </div>
    </Link>
  );
}

function CryptoSparkline({
  data,
  direction,
  className,
}: {
  data: CryptoKlinePoint[];
  direction: 'up' | 'down';
  className?: string;
}) {
  const width = 214;
  const height = 59;
  const stroke = direction === 'up' ? '#14C784' : '#FF2D2D';
  const path = createCryptoSparklinePath(data, width, height);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      fill="none"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      {path ? (
        <path
          d={path}
          stroke={stroke}
          strokeWidth="1.62"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}

function normalizeCryptoSparkline(value: unknown): CryptoKlinePoint[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const point = item as Record<string, unknown>;
      const t = Number(point.t ?? point.timestamp);
      const p = Number(point.p ?? point.price);

      return Number.isFinite(t) && Number.isFinite(p) ? { t, p, timestamp: t, price: p } : null;
    })
    .filter((item): item is CryptoKlinePoint => item !== null);
}

function createCryptoSparklinePath(data: CryptoKlinePoint[], svgWidth: number, svgHeight: number) {
  const points = [...data].sort((a, b) => a.timestamp - b.timestamp);
  if (points.length < 2) return '';

  const minT = points[0].timestamp;
  const maxT = points[points.length - 1].timestamp;
  const prices = points.map((point) => point.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const timeRange = maxT - minT || 1;
  const priceRange = maxP - minP || Math.max(Math.abs(maxP) * 0.01, 1);

  return points
    .map((point, index) => {
      const x = ((point.timestamp - minT) / timeRange) * svgWidth;
      const y = svgHeight - ((point.price - minP) / priceRange) * svgHeight;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function MarketContent({ event, markets }: { event: any; markets: any[] }) {
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const slug = event?.slug ?? '';
  const unlinkable = pathname === '/predic/market' && searchParams.get('id') === String(slug);
  const isClosed = event?.closed === 1;

  const market = isClosed
    ? markets?.[0]
    : (markets?.find((item) => item?.market_closed !== 1) ?? markets?.[0]);

  if (!market) return null;

  const handleMarket = (e: MouseEvent<HTMLAnchorElement>) => {
    if (slug && !unlinkable) {
      e.preventDefault();
      setDrawerWidth(600);
      openDrawer(<MarketDetail id={slug} openInSidebar />);
    }
  };

  const yesPrice = toNumber(market.outcome_yes_price);
  const change24h = toNumber(market.price_change_24h ?? 0);
  const totalVolume = toNumber(event?.volume_24hr ?? market.volume_24h);

  const cardMarket = {
    marketEventId: event?.id != null ? String(event.id) : undefined,
    imageSrc: market.market_icon || market.market_image || event?.icon || event?.image || '',
    imageAlt: event?.title || market.market_question || '',
    title: event?.title || market.market_question || '',
    outcome: market.group_item_title || 'YES',
    probability: yesPrice * 100,
    change: change24h * 100,
    changePeriod: '(24h)',
    volume: totalVolume,
    category: event?.category || '',
    statusLabel: getMarketStatus(market),
    resolvedOutcome: isMarketResolved(market) ? getMarketOutcome(market) : undefined,
  };

  return (
    <Link
      href={`/predic/market?id=${slug}`}
      className={cn('block', { 'cursor-default': unlinkable })}
      onClick={handleMarket}
    >
      <MarketCard
        marketEventId={cardMarket.marketEventId ?? ''}
        market={cardMarket}
        showBookmark={false}
        linkEnabled={false}
      />
    </Link>
  );
}

function NewsContent({ news }: { news: any }) {
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const unlinkable = pathname === '/predic/news' && searchParams.get('id') === String(news.news_id);

  const handleLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (news.news_id && !unlinkable) {
      e.preventDefault();
      setDrawerWidth(600);
      openDrawer(<NewsDetail id={news.news_id} openInSidebar />);
    }
  };

  return (
    <Link
      href={`/predic/news?id=${news.news_id}`}
      className={cn('block p-4', { 'cursor-default': unlinkable })}
      onClick={handleLink}
    >
      <NewsCard
        newsId={news.news_id}
        newsTitle={news.title}
        source={news.source}
        sourceIconUrl={news.source_icon_url || 'xxx.png'}
        sourceUrl={news.source_url}
        publishTime={news.publish_time}
        importance={news.importance_level}
        questions={news.recommended_questions}
      />
    </Link>
  );
}

function PortfolioContent({ portfolio }: { portfolio: any }) {
  const { t } = useTranslation();
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const pathname = usePathname();
  const unlinkable = pathname === '/predic/portfolio';

  const handleLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!unlinkable) {
      e.preventDefault();
      setDrawerWidth(600);
      openDrawer(<PortfolioPage openInSidebar />);
    }
  };

  if (!portfolio) return null;

  const totalValue = portfolio.totalValue ?? portfolio.currentValue ?? portfolio.stats?.capital;
  const changePercentage = portfolio.change24h?.percentage ?? portfolio.priceChange24h ?? 0;
  const changeValue = portfolio.change24h?.value ?? portfolio.priceChangeAmount ?? 0;
  const marketsCount = portfolio.marketsCount ?? portfolio.stats?.marketsCount ?? 0;
  const positionsTotalValue =
    portfolio.positionsTotalValue ?? portfolio.stats?.capital ?? portfolio.totalValue ?? 0;
  const unrealizedPnlPercentage =
    portfolio.unrealizedPnlPercentage ?? portfolio.stats?.unrealizedPercent ?? 0;
  const positiveStatus = getPricePositive(changePercentage);
  const unrealizedPositiveStatus = getPricePositive(unrealizedPnlPercentage);

  return (
    <Link
      href="/predic/portfolio"
      className={cn('block p-4', { 'cursor-default': unlinkable })}
      onClick={handleLink}
    >
      <div className="space-y-3">
        <div className="text-lg font-medium">{t('Portfolio')}</div>
        <div className="flex items-center gap-3">
          <div className="text-lg font-semibold leading-7">{currencyFormatter(totalValue)}</div>
          <div
            className={cn('text-xs text-[#757575] space-x-1', {
              'text-[#14C784]': positiveStatus === 2,
              'text-[#FF6B6B]': positiveStatus === 1,
              'text-[#757575]': positiveStatus === 0,
            })}
          >
            <span>
              {positiveStatus === 2 && '+'}
              {positiveStatus === 1 && '-'}
              {Math.abs(changePercentage).toFixed(2)}%
            </span>
            <span>({currencyFormatter(changeValue)})</span>
            <span className="text-[#757575]">{t('24h')}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center justify-between flex-1">
            <div className="flex flex-col items-end">
              <span className="text-sm leading-7">
                {numberFormatter(marketsCount, {
                  trailingZeroDisplay: 'stripIfInteger',
                })}
              </span>
              <span className="text-xs text-[#757575]">{t('Predictions')}</span>
            </div>
            <div className="bg-white/15 w-px mx-2 self-stretch" />
            <div className="flex flex-col items-end">
              <span className="text-sm leading-7">{currencyFormatter(positionsTotalValue)}</span>
              <span className="text-xs text-[#757575]">{t('Capital')}</span>
            </div>
            <div className="bg-white/15 w-px mx-2 self-stretch" />
            <div className="flex flex-col items-end">
              <span
                className={cn(`text-sm leading-7 inline-flex items-center gap-1`, {
                  'text-[#14C784]': unrealizedPositiveStatus === 2,
                  'text-[#FF6B6B]': unrealizedPositiveStatus === 1,
                  'text-[#757575]': unrealizedPositiveStatus === 0,
                })}
              >
                {unrealizedPnlPercentage.toFixed(2)}%
              </span>
              <span className="text-xs text-[#757575]">{t('PNL')}</span>
            </div>
          </div>
          {!unlinkable && (
            <span className="p-1 grow-0 ml-2 text-white rounded-lg border border-[#3A3838]">
              <ChevronRight size={20} />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function MarketTopicContent({ event }: { event: any }) {
  const { t } = useTranslation();
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const unlinkable =
    pathname === '/predic/event' && searchParams.get('id') === String(event.event_id);

  const imageUrl = findImageUrl(event);
  const totalInteractions = +(event.social_signal?.total_interactions || 0);
  const hasChangeIn24h = event.social_signal?.change_in_24h !== undefined;
  const changeIn24h = +(event.social_signal?.change_in_24h || 0);

  const handleLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (event.event_id && !unlinkable) {
      e.preventDefault();
      setDrawerWidth(600);
      openDrawer(<MarketTopic id={event.event_id} position="right" />);
    }
  };

  return (
    <Link
      className={cn('block', { 'cursor-default': unlinkable })}
      style={{
        background: imageUrl
          ? `url(${imageUrl}) no-repeat center/cover`
          : 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      }}
      href={`/predic/event?id=${event.event_id}`}
      onClick={handleLink}
    >
      <div className="pt-8 pb-4 px-4 h-full bg-linear-to-t from-black from-20% to-transparent">
        <div className="flex items-center text-sm leading-6">
          {event.category && (
            <span className="bg-white rounded-sm h-6 px-2 capitalize text-black">
              {event.category.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <h5 className="text-[18px] leading-5.5 font-bold mt-3 line-clamp-2">{event.title}</h5>
        <div className="flex items-center gap-3 mt-3">
          <div className="flex items-center gap-2 text-white">
            <Podcast size={14} strokeWidth={2} />
            <span className="text-[11px] leading-5 font-medium tracking-normal uppercase">
              {t('SOCIAL ENGAGEMENT')}
            </span>
          </div>
          <div className="flex items-center gap-1 whitespace-nowrap">
            <span className="text-xl leading-none font-bold text-[#14C784]">
              {totalInteractions === 0 ? '-' : formatCompactValue(totalInteractions)}
            </span>
            {hasChangeIn24h && (
              <span className="text-xs leading-none font-middle text-[#757575]">
                {formatCompactValue(changeIn24h)} ({t('24h')})
              </span>
            )}
          </div>
          {!unlinkable && (
            <span className="ml-auto p-1 text-white rounded-lg border border-[#3A3838]">
              <ChevronRight size={20} />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
